import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Plus, ChevronDown, Send, Move, ZoomIn, ZoomOut, Maximize2, User, LogOut, X, GripVertical, Bot } from "lucide-react";
import { chatService } from "../services/chat-service";
import { ImageUpload } from "../components/image-upload";
import { ThinkingIndicator } from "../components/thinking-indicator";
import type { SupportedModel, MessageContent } from "../types/session";

// 可创建的人格模板
const personaTemplates = [
  { id: "default", icon: "🤖", name: "Persona AI", color: "gray" },
  { id: "pm", icon: "📝", name: "产品经理", color: "blue" },
  { id: "designer", icon: "🎨", name: "设计师", color: "purple" },
  { id: "developer", icon: "💻", name: "程序员", color: "green" },
  { id: "analyst", icon: "📊", name: "分析师", color: "orange" },
  { id: "mentor", icon: "🎓", name: "导师", color: "indigo" },
  { id: "translator", icon: "🌐", name: "翻译官", color: "cyan" },
];

interface Message {
  id: string;
  role: "user" | "assistant";
  content: MessageContent;
  type?: "text" | "image";
  imageUrl?: string;
}

interface PersonaNode {
  id: string;
  personaId: string;
  name: string;
  icon: string;
  color: string;
  model: string;
  x: number;
  y: number;
  width: number;
  height: number;
  messages: Message[];
  context?: string; // 存储之前其他节点的对话上下文（不显示在界面上）
}

interface Connection {
  from: string;
  to: string;
}

const models = [
  { id: "deepseek-v3", name: "DeepSeek V3", multimodal: false },
  { id: "deepseek-v2", name: "DeepSeek V2", multimodal: false },
  { id: "hunyuan", name: "混元", multimodal: false },
  { id: "gpt-4o", name: "GPT-4o (多模态)", multimodal: true },
  { id: "gpt-4o-mini", name: "GPT-4o Mini (多模态)", multimodal: true },
  { id: "qwen-vl-max", name: "通义千问 VL (多模态)", multimodal: true },
  { id: "glm-4v", name: "GLM-4V (多模态)", multimodal: true },
];

export function ChatPage() {
  const location = useLocation();
  const state = location.state as { 
    initialMessage?: string; 
    selectedModel?: string;
    persona?: {
      id: string;
      icon: string;
      name: string;
      role?: string;
      description?: string;
    };
    selectedImage?: { url: string; base64: string } | null;
  } | null;
  
  // 初始化为空数组
  const [nodes, setNodes] = useState<PersonaNode[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  
  // 标记是否已处理初始状态
  const initialStateProcessedRef = useRef(false);

  const [showPersonaSelector, setShowPersonaSelector] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<Record<string, { url: string; base64: string } | null>>({});
  const [selectedConnectionNodes, setSelectedConnectionNodes] = useState<string[]>([]); // 选择的连接节点
  const [selectedPersonaModel, setSelectedPersonaModel] = useState<string>(models[0].id); // 添加人格时选择的模型

  // 画布状态
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);

  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const messageAreaRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // 自动调整 textarea 高度
  useEffect(() => {
    Object.keys(inputs).forEach((nodeId) => {
      const textarea = textareaRefs.current[nodeId];
      if (textarea) {
        textarea.style.height = "auto";
        const newHeight = Math.min(textarea.scrollHeight, 120);
        textarea.style.height = `${newHeight}px`;
      }
    });
  }, [inputs]);

  // 自动滚动到最新消息（平滑滚动）
  useEffect(() => {
    nodes.forEach((node) => {
      const messageArea = messageAreaRefs.current[node.id];
      if (messageArea && node.messages.length > 0) {
        requestAnimationFrame(() => {
          messageArea.scrollTo({
            top: messageArea.scrollHeight,
            behavior: "smooth",
          });
        });
      }
    });
  }, [nodes]);

  // 处理首页传来的初始状态
  useEffect(() => {
    if (initialStateProcessedRef.current || !state) return;
    
    // 标记已处理，防止重复执行
    initialStateProcessedRef.current = true;
    
    const containerWidth = containerRef.current?.clientWidth || 800;
    const containerHeight = containerRef.current?.clientHeight || 600;
    const centerX = (containerWidth / 2 - 160);
    const centerY = (containerHeight / 2 - 200);
    
    // 如果从首页选择了人格
    if (state.persona) {
      const persona = state.persona;
      const newNode: PersonaNode = {
        id: `node-${Date.now()}`,
        personaId: persona.id,
        name: persona.name,
        icon: persona.icon,
        color: "gray", // 默认颜色
        model: state.selectedModel || "deepseek-v3",
        x: centerX,
        y: centerY,
        width: 320,
        height: 500,
        messages: [],
      };
      setNodes([newNode]);
    }
    
    // 如果从首页输入了内容或有图片
    if (state.initialMessage || state.selectedImage) {
      // 获取选中的人格，如果没有则使用默认
      const persona = state.persona || personaTemplates[0];
      const nodeId = `node-${Date.now()}`;
      
      // 构建多模态消息内容
      const messageContent: MessageContent = state.selectedImage
        ? [
            { type: "text" as const, text: state.initialMessage || "请描述这张图片" },
            { type: "image_url" as const, image_url: { url: state.selectedImage.url } },
          ]
        : state.initialMessage || "";
      
      const userMessage: Message = {
        id: Date.now().toString(),
        role: "user",
        content: messageContent,
      };
      
      const newNode: PersonaNode = {
        id: nodeId,
        personaId: persona.id,
        name: persona.name,
        icon: persona.icon,
        color: "gray", // 默认颜色
        model: state.selectedModel || "deepseek-v3",
        x: centerX,
        y: centerY,
        width: 320,
        height: 500,
        messages: [userMessage],
      };
      setNodes([newNode]);
      
      // 自动调用 API 获取回复
      const model = state.selectedModel || "deepseek-v3";
      const personaId = persona.id;
      
      setTimeout(() => {
        handleAutoReply(nodeId, messageContent, personaId, model);
      }, 100);
    }
  }, [state]);

  // 画布拖拽
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === containerRef.current || (e.target as HTMLElement).classList.contains('canvas-area')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - canvasOffset.x, y: e.clientY - canvasOffset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setCanvasOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 拖拽节点
  const [draggingNode, setDraggingNode] = useState<{ nodeId: string; startX: number; startY: number; nodeStartX: number; nodeStartY: number } | null>(null);

  const startDragNode = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    setDraggingNode({
      nodeId,
      startX: e.clientX,
      startY: e.clientY,
      nodeStartX: node.x,
      nodeStartY: node.y,
    });
  };

  const handleDragNode = (e: React.MouseEvent) => {
    if (!draggingNode) return;
    
    const deltaX = (e.clientX - draggingNode.startX) / scale;
    const deltaY = (e.clientY - draggingNode.startY) / scale;
    
    setNodes(prevNodes => prevNodes.map(n => 
      n.id === draggingNode.nodeId
        ? { ...n, x: draggingNode.nodeStartX + deltaX, y: draggingNode.nodeStartY + deltaY }
        : n
    ));
  };

  const endDragNode = () => {
    setDraggingNode(null);
  };

  // 调整节点大小
  const [resizing, setResizing] = useState<{ nodeId: string; startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);

  const startResize = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    setResizing({
      nodeId,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: node.width,
      startHeight: node.height,
    });
  };

  const handleResize = (e: React.MouseEvent) => {
    if (!resizing) return;
    
    const deltaX = e.clientX - resizing.startX;
    const deltaY = e.clientY - resizing.startY;
    
    setNodes(prevNodes => prevNodes.map(n => 
      n.id === resizing.nodeId
        ? { ...n, width: Math.max(280, resizing.startWidth + deltaX), height: Math.max(300, resizing.startHeight + deltaY) }
        : n
    ));
  };

  const endResize = () => {
    setResizing(null);
  };

  // 重置画布
  const resetCanvas = () => {
    setCanvasOffset({ x: 0, y: 0 });
    setScale(1);
  };

  // 缩放
  const zoomIn = () => setScale((s) => Math.min(s + 0.1, 2));
  const zoomOut = () => setScale((s) => Math.max(s - 0.1, 0.5));

  // Ctrl + 滚轮缩放画布
  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (e.deltaY < 0) {
        zoomIn();
      } else {
        zoomOut();
      }
    }
  };

  // 阻止浏览器默认的 Ctrl+滚轮缩放
  useEffect(() => {
    const preventBrowserZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };
    const preventSpaceScroll = (e: KeyboardEvent) => {
      if (e.code === "Space" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
      }
    };
    
    window.addEventListener("wheel", preventBrowserZoom, { passive: false });
    window.addEventListener("keydown", preventSpaceScroll);
    
    return () => {
      window.removeEventListener("wheel", preventBrowserZoom);
      window.removeEventListener("keydown", preventSpaceScroll);
    };
  }, []);

  // 删除节点
  const deleteNode = (nodeId: string) => {
    setNodes(nodes.filter((n) => n.id !== nodeId));
    setConnections(connections.filter((c) => c.from !== nodeId && c.to !== nodeId));
  };

  // 添加人格节点
  const addPersonaNode = (template: typeof personaTemplates[0], modelId?: string) => {
    const containerWidth = containerRef.current?.clientWidth || 800;
    const containerHeight = containerRef.current?.clientHeight || 600;
    
    // 计算画布中心点（考虑拖拽偏移和缩放）
    const centerX = (containerWidth / 2 - canvasOffset.x) / scale;
    const centerY = (containerHeight / 2 - canvasOffset.y) / scale;
    
    // 根据选择的连接节点计算新节点位置
    let newNodeX = centerX - 160;
    let newNodeY = centerY - 200;
    
    if (selectedConnectionNodes.length > 0) {
      // 找到所有连接节点的平均位置
      const connectedNodes = nodes.filter(n => selectedConnectionNodes.includes(n.id));
      if (connectedNodes.length > 0) {
        const avgX = connectedNodes.reduce((sum, n) => sum + n.x, 0) / connectedNodes.length;
        const avgY = connectedNodes.reduce((sum, n) => sum + n.y, 0) / connectedNodes.length;
        newNodeX = avgX + 400;
        newNodeY = avgY;
      }
    } else if (nodes.length > 0) {
      // 如果没有选择连接节点，放在最后一个节点右边
      const lastNode = nodes[nodes.length - 1];
      newNodeX = lastNode.x + 400;
      newNodeY = lastNode.y;
    }
    
    // 收集选择节点的对话历史作为上下文（不显示在界面上）
    let contextText = "";
    const nodesToInclude = selectedConnectionNodes.length > 0 
      ? nodes.filter(n => selectedConnectionNodes.includes(n.id))
      : [];
    
    nodesToInclude.forEach((node) => {
      if (node.messages.length > 0) {
        contextText += `\n\n【${node.name}的对话记录】\n`;
        node.messages.forEach((msg) => {
          const role = msg.role === "user" ? "用户" : node.name;
          const content = typeof msg.content === "string" 
            ? msg.content 
            : Array.isArray(msg.content) 
              ? msg.content.find(item => item.type === "text")?.text || "[图片]"
              : "";
          contextText += `${role}: ${content}\n`;
        });
      }
    });
    
    const newNode: PersonaNode = {
      id: `node-${Date.now()}`,
      personaId: template.id,
      name: template.name,
      icon: template.icon,
      color: template.color,
      model: modelId || selectedPersonaModel, // 使用选择的模型或传入的模型
      x: newNodeX,
      y: newNodeY,
      width: 320,
      height: 500,
      messages: [], // 消息列表为空
      context: contextText.trim(), // 隐藏的上下文信息
    };
    setNodes([...nodes, newNode]);

    // 创建连接线（从选择的节点连向新节点）
    const newConnections = selectedConnectionNodes.map(nodeId => ({
      from: nodeId,
      to: newNode.id
    }));
    setConnections([...connections, ...newConnections]);

    setShowPersonaSelector(false);
    setSelectedConnectionNodes([]); // 清空选择
    setSelectedPersonaModel(models[0].id); // 重置模型选择
  };

  // 创建分支连接
  const startConnection = (nodeId: string) => {
    if (connectingFrom === nodeId) {
      setConnectingFrom(null);
    } else if (connectingFrom) {
      // 完成连接
      if (connectingFrom !== nodeId) {
        // 检查是否已存在连接
        const exists = connections.some(c => c.from === connectingFrom && c.to === nodeId);
        if (!exists) {
          setConnections([...connections, { from: connectingFrom, to: nodeId }]);
        }
      }
      setConnectingFrom(null);
    } else {
      setConnectingFrom(nodeId);
    }
  };

  // 自动回复（用于首页跳转）
  const handleAutoReply = async (nodeId: string, content: MessageContent, personaId: string, model: string, context?: string) => {
    const aiMessageId = Date.now().toString();
    
    // 先添加空的 AI 消息
    setNodes(prevNodes => prevNodes.map(n => 
      n.id === nodeId
        ? { ...n, messages: [...n.messages, { id: aiMessageId, role: "assistant" as const, content: "" }] }
        : n
    ));

    // 调用 API
    try {
      await chatService.streamReply(
        {
          sessionId: nodeId,
          model: model as SupportedModel,
          personaId,
          messages: [{ id: Date.now().toString(), role: "user" as const, content, createdAt: new Date().toISOString() }],
          context, // 传递上下文
        },
        (chunk) => {
          setNodes(prevNodes =>
            prevNodes.map(n =>
              n.id === nodeId
                ? {
                    ...n,
                    messages: n.messages.map((m) =>
                      m.id === aiMessageId
                        ? { ...m, content: m.content + chunk }
                        : m
                    ),
                  }
                : n
            )
          );
        }
      );
    } catch (error) {
      console.error("API error:", error);
    }
  };

  // 发送消息
  const sendMessage = async (nodeId: string, content: MessageContent) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content,
    };

    // 先添加用户消息，AI 消息先为空
    const aiMessageId = (Date.now() + 1).toString();
    const aiResponse: Message = {
      id: aiMessageId,
      role: "assistant",
      content: "",
    };

    // 更新节点，添加用户消息和空的 AI 消息
    setNodes(
      nodes.map((n) =>
        n.id === nodeId
          ? { ...n, messages: [...n.messages, userMessage, aiResponse] }
          : n
      )
    );
    setInputs({ ...inputs, [nodeId]: "" });
    setSelectedImages({ ...selectedImages, [nodeId]: null });

    // 调用真实 API
    try {
      await chatService.streamReply(
        {
          sessionId: nodeId,
          model: node.model as SupportedModel,
          personaId: node.personaId,
          messages: [...node.messages, userMessage].map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            createdAt: new Date().toISOString(),
          })),
          context: node.context, // 传递上下文
        },
        (chunk) => {
          // 流式更新 AI 消息内容
          setNodes((prevNodes) =>
            prevNodes.map((n) =>
              n.id === nodeId
                ? {
                    ...n,
                    messages: n.messages.map((m) =>
                      m.id === aiMessageId
                        ? { ...m, content: m.content + chunk }
                        : m
                    ),
                  }
                : n
            )
          );
        }
      );
    } catch (error) {
      console.error("API error:", error);
      // 错误时显示提示
      setNodes((prevNodes) =>
        prevNodes.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                messages: n.messages.map((m) =>
                  m.id === aiMessageId
                    ? { ...m, content: "抱歉，API 调用失败，请检查配置。" }
                    : m
                ),
              }
            : n
        )
      );
    }
  };

  const getColorClass = (color: string) => {
    const colors: Record<string, { border: string; header: string; text: string }> = {
      gray: { border: "border-gray-200", header: "bg-gray-50", text: "text-gray-700" },
      blue: { border: "border-blue-200", header: "bg-blue-50", text: "text-blue-700" },
      purple: { border: "border-purple-200", header: "bg-purple-50", text: "text-purple-700" },
      green: { border: "border-green-200", header: "bg-green-50", text: "text-green-700" },
      orange: { border: "border-orange-200", header: "bg-orange-50", text: "text-orange-700" },
      indigo: { border: "border-indigo-200", header: "bg-indigo-50", text: "text-indigo-700" },
      cyan: { border: "border-cyan-200", header: "bg-cyan-50", text: "text-cyan-700" },
    };
    return colors[color] || colors.gray;
  };

  // 计算连接线坐标（从节点右侧中心连到左侧中心）
  const getConnectionPath = (from: PersonaNode, to: PersonaNode) => {
    const startX = from.x + from.width;
    const startY = from.y + 40;
    const endX = to.x;
    const endY = to.y + 40;
    const midX = (startX + endX) / 2;
    return `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {/* 顶部导航 */}
      <header className="h-14 border-b border-gray-200 bg-white flex items-center justify-between px-6 shrink-0 z-50">
        <div className="flex items-center gap-4">
          <Link to="/" className="font-semibold text-lg text-gray-900">
            AI Chat
          </Link>
        </div>

        {/* 右侧账号区域 */}
        <div className="flex items-center gap-4">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
            最近对话
          </Link>
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition"
            >
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-gray-600" />
              </div>
              <span className="text-sm text-gray-700">用户</span>
            </button>
            {showUserMenu && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50">
                <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">
                  个人设置
                </button>
                <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">
                  我的人格
                </button>
                <div className="border-t border-gray-100"></div>
                <button className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2">
                  <LogOut className="w-4 h-4" />
                  退出登录
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* 无限画布区域 */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden relative cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={(e) => {
          handleMouseMove(e);
          handleResize(e);
          handleDragNode(e);
        }}
        onMouseUp={() => {
          handleMouseUp();
          endResize();
          endDragNode();
        }}
        onMouseLeave={() => {
          handleMouseUp();
          endResize();
          endDragNode();
        }}
        onWheel={handleWheel}
      >
        {/* 画布内容 */}
        <div
          className="canvas-area absolute"
          style={{
            transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${scale})`,
            transformOrigin: "0 0",
          }}
        >
          {/* 背景网格 */}
          <div
            className="absolute pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle, #d1d5db 1px, transparent 1px)`,
              backgroundSize: "40px 40px",
              width: "10000px",
              height: "10000px",
              left: "-5000px",
              top: "-5000px",
            }}
          />

          {/* SVG 连接线层 - 使用贝塞尔曲线 */}
          {/* 连接线放在节点下层，z-index 低于节点 */}
          {/* SVG 覆盖足够大的区域以支持节点连接 */}
          <svg
            className="absolute pointer-events-none"
            style={{
              width: "4000px",
              height: "4000px",
              left: "-1500px",
              top: "-1500px",
              zIndex: 50,
            }}
          >
            <defs>
              {/* 箭头标记 */}
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
              </marker>
            </defs>
            {connections.map((conn, index) => {
              const fromNode = nodes.find((n) => n.id === conn.from);
              const toNode = nodes.find((n) => n.id === conn.to);
              if (!fromNode || !toNode) return null;

              // 从节点右侧中心连接到左侧中心
              // 坐标需要加上 SVG 的偏移量（1500px）
              const startX = fromNode.x + fromNode.width + 1500;
              const startY = fromNode.y + 40 + 1500;
              const endX = toNode.x + 1500;
              const endY = toNode.y + 40 + 1500;

              // 贝塞尔曲线控制点
              const controlOffset = Math.abs(endX - startX) * 0.5;
              const cp1x = startX + controlOffset;
              const cp1y = startY;
              const cp2x = endX - controlOffset;
              const cp2y = endY;

              return (
                <g key={index}>
                  {/* 阴影效果 */}
                  <path
                    d={`M ${startX} ${startY} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${endX} ${endY}`}
                    fill="none"
                    stroke="rgba(59, 130, 246, 0.2)"
                    strokeWidth="6"
                    strokeLinecap="round"
                  />
                  {/* 主连接线 */}
                  <path
                    d={`M ${startX} ${startY} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${endX} ${endY}`}
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    markerEnd="url(#arrowhead)"
                  />
                  {/* 起点圆点 */}
                  <circle cx={startX} cy={startY} r="5" fill="#3b82f6" />
                </g>
              );
            })}
          </svg>

          {/* 空状态 - 提示添加人格 */}
          {nodes.length === 0 && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center w-80">
              <div className="text-6xl mb-4">🎨</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                开始创作
              </h2>
              <p className="text-gray-500 mb-6 text-sm leading-relaxed">
                添加人格节点，构建你的 AI 协作工作流
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPersonaSelector(true);
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition whitespace-nowrap"
              >
                <Plus className="w-4 h-4" />
                <span>添加人格</span>
              </button>
            </div>
          )}

          {/* 人格节点 */}
          {nodes.map((node) => {
            const colorClass = getColorClass(node.color);
            const isConnecting = connectingFrom === node.id;

            return (
              <div
                key={node.id}
                className="absolute cursor-default"
                style={{ left: node.x, top: node.y, zIndex: 100 }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseEnter={(e) => e.stopPropagation()}
              >
                {/* 节点卡片 */}
                <div
                  className={`bg-white border-2 ${isConnecting ? "border-gray-900" : colorClass.border} rounded-2xl shadow-lg overflow-hidden flex flex-col ${resizing?.nodeId === node.id ? "" : "transition-all duration-200"}`}
                  style={{ width: node.width, height: node.height }}
                >
                  {/* 头部（可拖拽） */}
                  <div 
                    className={`px-4 py-3 border-b ${colorClass.border} ${colorClass.header} flex items-center justify-between shrink-0 cursor-move select-none`}
                    onMouseDown={(e) => startDragNode(e, node.id)}
                  >
                    <div className="flex items-center gap-3">
                      {/* 人格头像：支持 emoji 或图片 */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden ${
                        (node.icon.startsWith("http") || node.icon.startsWith("data:"))
                          ? ""
                          : colorClass.text.includes("blue") ? "bg-blue-100" :
                            colorClass.text.includes("purple") ? "bg-purple-100" :
                            colorClass.text.includes("green") ? "bg-green-100" :
                            colorClass.text.includes("orange") ? "bg-orange-100" :
                            colorClass.text.includes("indigo") ? "bg-indigo-100" : "bg-cyan-100"
                      }`}>
                        {node.icon.startsWith("http") || node.icon.startsWith("data:") ? (
                          <img src={node.icon} alt={node.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg">{node.icon}</span>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <div className="font-medium text-gray-800">
                          {node.name}
                        </div>
                        <div className="text-xs text-gray-400">
                          {models.find(m => m.id === node.model)?.name || node.model}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-400">{node.messages.length} 条</span>
                      {/* 连接按钮 */}
                      <button
                        onClick={() => startConnection(node.id)}
                        className={`w-6 h-6 rounded flex items-center justify-center text-xs transition ${
                          isConnecting ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                        title={isConnecting ? "点击目标节点完成连接" : "点击开始连接"}
                      >
                        +
                      </button>
                      {/* 删除按钮 */}
                      <button
                        onClick={() => deleteNode(node.id)}
                        className="w-6 h-6 rounded flex items-center justify-center text-xs bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-500 transition"
                        title="删除"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* 消息区域 */}
                  <div
                    ref={(el) => (messageAreaRefs.current[node.id] = el)}
                    className="flex-1 overflow-y-auto p-4 space-y-3"
                  >
                    {node.messages.length === 0 && (
                      <div className="text-center text-gray-400 text-sm py-8">
                        开始对话...
                      </div>
                    )}
                    {node.messages.map((msg) => {
                      // 如果是多模态消息,拆分为图片和文本两条独立消息
                      if (typeof msg.content !== "string" && Array.isArray(msg.content)) {
                        const imageItems = msg.content.filter(item => item.type === "image_url");
                        const textItem = msg.content.find(item => item.type === "text");

                        return (
                          <div key={msg.id} className="space-y-2">
                            {/* 图片消息 */}
                            {imageItems.map((item, idx) => {
                              if (item.type === "image_url") {
                                return (
                                  <div
                                    key={`${msg.id}-img-${idx}`}
                                    className={`flex items-start gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
                                  >
                                    {/* 头像 */}
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                                      msg.role === "user"
                                        ? "bg-blue-500"
                                        : colorClass.text.includes("blue") ? "bg-blue-100" :
                                          colorClass.text.includes("purple") ? "bg-purple-100" :
                                          colorClass.text.includes("green") ? "bg-green-100" :
                                          colorClass.text.includes("orange") ? "bg-orange-100" :
                                          colorClass.text.includes("indigo") ? "bg-indigo-100" :
                                          colorClass.text.includes("cyan") ? "bg-cyan-100" : "bg-gray-100"
                                    }`}>
                                      {msg.role === "user" ? (
                                        <User className="w-3.5 h-3.5 text-white" />
                                      ) : (
                                        <Bot className={`w-3.5 h-3.5 ${colorClass.text}`} />
                                      )}
                                    </div>
                                    {/* 图片 */}
                                    <img
                                      src={item.image_url.url}
                                      alt="用户上传的图片"
                                      className="max-w-[200px] max-h-[120px] object-cover rounded-xl shadow-sm"
                                    />
                                  </div>
                                );
                              }
                              return null;
                            })}
                            {/* 文本消息 */}
                            {(textItem && textItem.text) || (msg.role === "assistant" && !textItem) ? (
                              <div
                                className={`flex items-start gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
                              >
                                {/* 头像 */}
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                                  msg.role === "user"
                                    ? "bg-blue-500"
                                    : colorClass.text.includes("blue") ? "bg-blue-100" :
                                      colorClass.text.includes("purple") ? "bg-purple-100" :
                                      colorClass.text.includes("green") ? "bg-green-100" :
                                      colorClass.text.includes("orange") ? "bg-orange-100" :
                                      colorClass.text.includes("indigo") ? "bg-indigo-100" :
                                      colorClass.text.includes("cyan") ? "bg-cyan-100" : "bg-gray-100"
                                }`}>
                                  {msg.role === "user" ? (
                                    <User className="w-3.5 h-3.5 text-white" />
                                  ) : (
                                    <Bot className={`w-3.5 h-3.5 ${colorClass.text}`} />
                                  )}
                                </div>
                                {/* 文本 */}
                                <div
                                  className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                                    msg.role === "user"
                                      ? "bg-gray-900 text-white"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {/* AI 思考中状态 */}
                                  {msg.role === "assistant" && !textItem?.text ? (
                                    <ThinkingIndicator />
                                  ) : (
                                    <p className="whitespace-pre-wrap">{textItem?.text}</p>
                                  )}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      }

                      // 普通文本消息
                      return (
                        <div
                          key={msg.id}
                          className={`flex items-start gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
                        >
                          {/* 头像 */}
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                            msg.role === "user"
                              ? "bg-blue-500"
                              : colorClass.text.includes("blue") ? "bg-blue-100" :
                                colorClass.text.includes("purple") ? "bg-purple-100" :
                                colorClass.text.includes("green") ? "bg-green-100" :
                                colorClass.text.includes("orange") ? "bg-orange-100" :
                                colorClass.text.includes("indigo") ? "bg-indigo-100" :
                                colorClass.text.includes("cyan") ? "bg-cyan-100" : "bg-gray-100"
                          }`}>
                            {msg.role === "user" ? (
                              <User className="w-3.5 h-3.5 text-white" />
                            ) : (
                              <Bot className={`w-3.5 h-3.5 ${colorClass.text}`} />
                            )}
                          </div>
                          {/* 消息内容 */}
                          <div
                            className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                              msg.role === "user"
                                ? "bg-gray-900 text-white"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {/* AI 思考中状态 */}
                            {msg.role === "assistant" && !msg.content ? (
                              <ThinkingIndicator />
                            ) : (
                              <p className="whitespace-pre-wrap">{msg.content}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* 输入区 */}
                  <div className="p-3 border-t border-gray-100 shrink-0">
                    {/* 图片预览 */}
                    {selectedImages[node.id] && (
                      <div className="mb-2">
                        <div className="relative inline-block">
                          <img
                            src={selectedImages[node.id]!.url}
                            alt="预览"
                            className="w-12 h-12 object-cover rounded border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => setSelectedImages({ ...selectedImages, [node.id]: null })}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                    {/* 输入框 */}
                    <div className="flex gap-2 items-end">
                      {/* 图片上传按钮 */}
                      <ImageUpload
                        onImageSelect={(imageData) => {
                          setSelectedImages({ ...selectedImages, [node.id]: imageData });
                        }}
                        disabled={false}
                      />
                      <textarea
                        ref={(el) => (textareaRefs.current[node.id] = el)}
                        value={inputs[node.id] || ""}
                        onChange={(e) => setInputs({ ...inputs, [node.id]: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey && (inputs[node.id]?.trim() || selectedImages[node.id])) {
                            e.preventDefault();
                            // 构建多模态消息
                            const content: MessageContent = selectedImages[node.id]
                              ? [
                                  { type: "text" as const, text: inputs[node.id] || "请描述这张图片" },
                                  { type: "image_url" as const, image_url: { url: selectedImages[node.id]!.url } },
                                ]
                              : inputs[node.id] || "";
                            sendMessage(node.id, content);
                          }
                        }}
                        placeholder={selectedImages[node.id] ? "输入问题...（图片已选择）" : "输入消息..."}
                        rows={1}
                        className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-300 resize-none overflow-y-auto min-h-[36px] max-h-[120px]"
                        style={{ lineHeight: "20px" }}
                      />
                      <button
                        onClick={() => {
                          if (inputs[node.id]?.trim() || selectedImages[node.id]) {
                            const content: MessageContent = selectedImages[node.id]
                              ? [
                                  { type: "text" as const, text: inputs[node.id] || "请描述这张图片" },
                                  { type: "image_url" as const, image_url: { url: selectedImages[node.id]!.url } },
                                ]
                              : inputs[node.id] || "";
                            sendMessage(node.id, content);
                          }
                        }}
                        className="px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition h-9"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* 调整大小手柄 */}
                  <div
                    className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize select-none"
                    onMouseDown={(e) => startResize(e, node.id)}
                  >
                    <svg className="w-4 h-4 text-gray-300" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M14 14H12V12H14V14ZM14 10H12V8H14V10ZM10 14H8V12H10V14ZM14 6H12V4H14V6ZM10 10H8V8H10V10ZM6 14H4V12H6V14Z" />
                    </svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 画布控制按钮 */}
        <div className="absolute bottom-6 right-6 flex flex-col gap-2">
          {/* 添加人格按钮 - 只在有节点时显示 */}
          {nodes.length > 0 && (
            <button
              onClick={() => {
                setSelectedConnectionNodes([]); // 清空选择，不自动连接
                setShowPersonaSelector(true);
              }}
              className="w-10 h-10 bg-blue-500 border border-blue-500 rounded-lg shadow-sm flex items-center justify-center hover:bg-blue-600 transition"
              title="添加人格"
            >
              <Plus className="w-4 h-4 text-white" />
            </button>
          )}
          <button onClick={zoomIn} className="w-10 h-10 bg-white border border-gray-200 rounded-lg shadow-sm flex items-center justify-center hover:bg-gray-50">
            <ZoomIn className="w-4 h-4 text-gray-600" />
          </button>
          <button onClick={zoomOut} className="w-10 h-10 bg-white border border-gray-200 rounded-lg shadow-sm flex items-center justify-center hover:bg-gray-50">
            <ZoomOut className="w-4 h-4 text-gray-600" />
          </button>
          <button onClick={resetCanvas} className="w-10 h-10 bg-white border border-gray-200 rounded-lg shadow-sm flex items-center justify-center hover:bg-gray-50">
            <Maximize2 className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* 提示 */}
        {nodes.length > 0 && (
          <div className="absolute bottom-6 left-6 text-xs text-gray-400 bg-white px-3 py-1.5 rounded-lg border border-gray-100 whitespace-nowrap">
            <Move className="w-3 h-3 inline mr-1" />
            拖拽节点头部移动 | 拖拽空白区域平移画布 | Ctrl+滚轮缩放
          </div>
        )}
      </div>

      {/* 人格选择器弹窗 */}
      {showPersonaSelector && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 m-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              添加人格
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              选择一个人格加入画布
            </p>
            
            {/* 模型选择器 - 放在最上方，更醒目 */}
            <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <label className="text-sm font-medium text-blue-900 mb-2 block flex items-center gap-2">
                <span>🤖</span>
                <span>选择大模型</span>
              </label>
              <select
                value={selectedPersonaModel}
                onChange={(e) => setSelectedPersonaModel(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-blue-200 rounded-lg text-sm font-medium outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 cursor-pointer transition"
              >
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} {model.multimodal && "🖼️ 支持图片"}
                  </option>
                ))}
              </select>
              <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                <span>💡</span>
                <span>带 🖼️ 标记的模型支持图片识别</span>
              </p>
            </div>
            
            {/* 选择连接节点 */}
            {nodes.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  选择要连接的节点（多选，可获取它们的聊天记录）
                </h4>
                <div className="flex flex-wrap gap-2">
                  {nodes.map((node) => {
                    const colorClass = getColorClass(node.color);
                    const isSelected = selectedConnectionNodes.includes(node.id);
                    return (
                      <button
                        key={node.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedConnectionNodes(selectedConnectionNodes.filter(id => id !== node.id));
                          } else {
                            setSelectedConnectionNodes([...selectedConnectionNodes, node.id]);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition ${
                          isSelected 
                            ? "border-blue-500 bg-blue-50 text-blue-700" 
                            : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        <span className="text-lg">{node.icon}</span>
                        <span className="text-sm font-medium">{node.name}</span>
                        {isSelected && (
                          <span className="ml-1 w-4 h-4 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedConnectionNodes.length === 0 && (
                  <p className="text-xs text-gray-400 mt-2">
                    未选择任何节点，新角色将不会获取任何聊天记录
                  </p>
                )}
              </div>
            )}
            
            {/* 人格选择 */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {personaTemplates.map((template) => {
                const colorClass = getColorClass(template.color);
                const currentModel = models.find(m => m.id === selectedPersonaModel);
                return (
                  <button
                    key={template.id}
                    onClick={() => addPersonaNode(template)}
                    className="flex flex-col items-start gap-2 p-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition text-left group"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        colorClass.text.includes("blue") ? "bg-blue-100" :
                        colorClass.text.includes("purple") ? "bg-purple-100" :
                        colorClass.text.includes("green") ? "bg-green-100" :
                        colorClass.text.includes("orange") ? "bg-orange-100" :
                        colorClass.text.includes("indigo") ? "bg-indigo-100" : "bg-cyan-100"
                      }`}>
                        <Bot className={`w-5 h-5 ${colorClass.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900">{template.name}</div>
                        <div className="text-xs text-gray-400 truncate group-hover:text-blue-600 transition">{currentModel?.name}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => {
                setShowPersonaSelector(false);
                setSelectedConnectionNodes([]);
                setSelectedPersonaModel(models[0].id);
              }}
              className="w-full mt-4 py-2.5 text-sm text-gray-500 hover:text-gray-700"
            >
              取消
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
